/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useMutation, useQuery } from 'react-query';
import { isBrowser } from 'react-device-detect';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';
import { BANK_REFERENCE } from '../../queries';
import { EntryPlaceholder } from '../../../../components/Placeholders';
import { BUY_FIAT, FUNDING_BANK_DETAILS_DOWNLOAD_PDF } from '../../mutations';
import PgwIframe from '../../../../components/PgwIframe';
import CurrencyIcon from '../../../../components/CurrencyIcon';
import { copyToClipboard, errorCodeIsRiskAssessmentWarning } from '../../../../utilities/helpers';
import { formatDecimalNumber } from '../../../../utilities/formatHelpers';
import { validateNumbers } from '../../../../utilities/formValidation';
import FocusableInputArea from '../../partials/FocusableInputArea';
import { AMOUNT_INPUT_MAX_LENGTH } from '../../constants';
import { showAlert } from '../../../../utilities/notifications';
import downloadFile from '../../../../utilities/downloadFile';
import Badge from '../../../../components/Badge';

import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_REGION_SETTINGS_QUERY_KEY,
} from '../../../../utilities/sharedQueries';
import { useLedgerFilteredCurrencies } from '../../../../utilities/sharedHooks/ledgerWallets';

const FiatWalletDeposit = ({ onCloseModal, options, refreshWallets }) => {
  const { t: translate } = useTranslation();
  const wallet = options ? options.wallet : null;
  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);
  const canFundAccountsWithBankTransfer = useMemo(
    () => userRegionSettings?.can_fund_accounts_with_bank_transfer || false,
    [userRegionSettings]
  );
  const [isBank, setIsBank] = useState(canFundAccountsWithBankTransfer);
  const [fiat, setFiat] = useState({
    amount: 0,
    displayAmount: '',
  });
  const [fiatInputWidth, setFiatInputWidth] = useState(0);
  const [pgwUrl, setPgwUrl] = useState(null);
  const { firstName, lastName } = useUserPersonalInfo();
  const [showPgwFooter, setShowPgwFooter] = useState(true);
  const { data: bankReference, isLoading: isLoadingReference } = useQuery([
    BANK_REFERENCE,
    { currency: wallet.coin },
  ]);

  const [showPgwWarning] = useSetting('ui_features_toggles', 'feature_allow_pgw_warning');
  const [pgwWarningMessage] = useSetting('ui_features_toggles', 'feature_pgw_warning_message');

  const [feauterPgwAllowed] = useSetting('app_features', 'feature_pgw_allowed');

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);
  const isAllowBuyPGW = useMemo(() => {
    if (supportedCurrencies && wallet?.coin) {
      return supportedCurrencies[wallet?.coin]
        ? supportedCurrencies[wallet?.coin].allows_buy_pgw
        : false;
    }
    return false;
  }, [supportedCurrencies, wallet?.coin]);

  const { mutate: buyFiat, isLoading: isLoadingPgw } = useMutation(BUY_FIAT, {
    onSettled: (data, error) => {
      if (error) {
        if (errorCodeIsRiskAssessmentWarning(error.errorCode)) {
          showAlert({
            message: error.message,
            type: 'warning',
          });
        } else {
          showAlert({ message: error.message, type: 'danger' });
        }
        setPgwUrl(null);
      } else if (data && data?.redirectUrl) {
        setShowPgwFooter(false);
        setPgwUrl(data.redirectUrl);
      } else {
        showAlert({ message: data, type: 'danger' });
      }
    },
  });

  const { mutate: downloadFundingBankDetails } = useMutation(FUNDING_BANK_DETAILS_DOWNLOAD_PDF, {
    onSettled(data, error) {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        downloadFile({
          data,
          fileName: `FundingBankDetails${wallet?.coin.toUpperCase()}`,
        });
      }
    },
  });

  const [amountChosen, setAmountChosen] = useState(false);

  const isLoading = false;

  const [isManualAmount, setIsManualAmount] = useState(false);
  const [fundingBankName] = useSetting('bank_funding_details', `funding_bank_name_${wallet.coin}`);
  const [fundingBankIBAN] = useSetting('bank_funding_details', `funding_bank_iban_${wallet.coin}`);
  const [fundingBankBIC] = useSetting('bank_funding_details', `funding_bank_bic_${wallet.coin}`);

  const [fundingBankSortCode] = useSetting(
    'bank_funding_details',
    `funding_bank_sort_code_${wallet.coin}`
  );
  const [fundingBankAccountNumber] = useSetting(
    'bank_funding_details',
    `funding_bank_account_number_${wallet.coin}`
  );

  const [fundingBankCountry] = useSetting(
    'bank_funding_details',
    `funding_bank_country_${wallet.coin}`
  );

  const renderTitle = () => (
    <b className="text-f5 text-dark me-auto">
      <CurrencyIcon currencyCode={wallet?.coin} className="me-2" />
      {translate('deposit_fiat_label').replace('%s', wallet.coin.toUpperCase())}
    </b>
  );

  const renderFooter = () => {
    if (!pgwUrl || !amountChosen) {
      return (
        <>
          {isBank ? (
            <button
              loading={isLoading ? 'true' : 'false'}
              type="button"
              className="btn btn-primary py-3"
              onClick={() => downloadFundingBankDetails({ currency: wallet?.coin })}>
              {translate('download_receipt_label')}
            </button>
          ) : (
            <button
              type="button"
              loading={isLoadingPgw ? 'true' : 'false'}
              className={`btn ${fiat.amount > 0 ? 'btn-primary' : 'btn-disabled'} py-3`}
              disabled={fiat.amount <= 0}
              onClick={() => {
                buyFiat({
                  amount: fiat.amount,
                  currency: wallet.coin,
                  displayCurrency: wallet.coin,
                  coin: wallet.coin,
                });
                setAmountChosen(true);
              }}>
              {translate('confirm_continue_label')}
            </button>
          )}
        </>
      );
    }
    if (pgwUrl && showPgwFooter) {
      return (
        <button type="button" className="btn btn-primary py-3" onClick={onCloseModal}>
          {translate('continue_btn_title')}
        </button>
      );
    }
    return <></>;
  };

  const renderBank = () => (
    <div>
      <p className="mb-4">{translate('copy_past_add_code_bank')}</p>
      <div>
        <p className="text-shadow text-f6">{translate('beneficiary_label')}</p>
        <p>
          <i
            onKeyDown={null}
            tabIndex={-1}
            role="button"
            aria-label="Copy"
            className="icon-copy-other text-primary text-f5 cursor-pointer"
            onClick={() => copyToClipboard(fundingBankName)}
          />{' '}
          {fundingBankName}
        </p>
      </div>
      <div className="mt-3">
        <p className="text-shadow text-f6">{translate('sort_code_label')}</p>
        <p>
          <i
            onKeyDown={null}
            tabIndex={-1}
            role="button"
            aria-label="Copy"
            className="icon-copy-other text-primary text-f5 cursor-pointer"
            onClick={() => copyToClipboard(fundingBankSortCode)}
          />{' '}
          {fundingBankSortCode}
        </p>
      </div>
      <div className="mt-3">
        <p className="text-shadow text-f6">{translate('account_number_label')}</p>
        <p>
          <i
            onKeyDown={null}
            tabIndex={-1}
            role="button"
            aria-label="Copy"
            className="icon-copy-other text-primary text-f5 cursor-pointer"
            onClick={() => copyToClipboard(fundingBankAccountNumber)}
          />{' '}
          {fundingBankAccountNumber}
        </p>
      </div>
      <div className="mt-3">
        <p className="text-shadow text-f6">{translate('IBAN_label')}</p>
        <p>
          <i
            onKeyDown={null}
            tabIndex={-1}
            role="button"
            aria-label="Copy"
            className="icon-copy-other text-primary text-f5 cursor-pointer"
            onClick={() => copyToClipboard(fundingBankIBAN)}
          />{' '}
          {fundingBankIBAN}
        </p>
      </div>
      <div className="mt-3">
        <p className="text-shadow text-f6">{translate('BIC_label')}</p>
        <p>
          <i
            onKeyDown={null}
            tabIndex={-1}
            role="button"
            aria-label="Copy"
            className="icon-copy-other text-primary text-f5 cursor-pointer"
            onClick={() => copyToClipboard(fundingBankBIC)}
          />{' '}
          {fundingBankBIC}
        </p>
      </div>
      <div className="mt-3">
        <p className="text-shadow text-f6">{translate('country_label')}</p>
        <p>
          <i
            onKeyDown={null}
            tabIndex={-1}
            role="button"
            aria-label="Copy"
            className="icon-copy-other text-primary text-f5 cursor-pointer"
            onClick={() => copyToClipboard(fundingBankCountry)}
          />{' '}
          {fundingBankCountry}
        </p>
      </div>
      <div className="mt-3">
        <p className="text-shadow text-f6">{translate('reference_deposit_fiat_label')}</p>
        {isLoadingReference ? (
          <EntryPlaceholder />
        ) : (
          <p>
            <i
              onKeyDown={null}
              tabIndex={-1}
              role="button"
              aria-label="Copy"
              className="icon-copy-other text-primary text-f5 cursor-pointer"
              onClick={() => copyToClipboard(bankReference)}
            />{' '}
            {bankReference}
          </p>
        )}
      </div>
      <div className="alert alert-info mt-3" role="alert">
        <i className="icon icon-info-icon" />
        <span className="content">
          {translate('deposit_fiat_note_label')} <b>{`${firstName} ${lastName}`}</b>
        </span>
      </div>
    </div>
  );

  const handleAmountChangeInPGW = (e) => {
    const { value, min } = e.target;

    const { decimalAmount, formattedAmount } = formatDecimalNumber(value, min);

    if (!validateNumbers(decimalAmount, wallet?.wallet_type)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiat({
        amount: 0,
        displayAmount: '',
      });
    }

    setFiat({
      amount: Number(decimalAmount),
      displayAmount: formattedAmount,
    });
  };

  const amountInputSpan = useRef();

  const handleFocus = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlur = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  useEffect(() => {
    if (amountInputSpan.current) {
      const closestContainer = amountInputSpan.current.closest('.d-flex');
      const closestContainerParent = closestContainer.closest('.modal-body');
      const charactersLength =
        amountInputSpan.current.value.length > 1 ? amountInputSpan.current.value.length : 2;
      // to avoid having empty input, fix length to 2 if text length is less then 1
      if (closestContainerParent.offsetWidth < closestContainer.scrollWidth) {
        closestContainer.style.fontSize = '1.5rem';
      } else if (amountInputSpan.current.value.length < AMOUNT_INPUT_MAX_LENGTH) {
        // resize input font size to 2.125rem when char length is less then AMOUNT_INPUT_MAX_LENGTH
        closestContainer.style.fontSize = '2.125rem';
      }
      setFiatInputWidth(charactersLength);
    }
  }, [fiat]);

  const renderPgw = () => {
    const PREDEFINED_AMOUNTS = [50, 100, 200, 500];
    return (
      <>
        {amountChosen && pgwUrl ? (
          <PgwIframe
            pgwUrl={pgwUrl}
            onPgwResolve={(isConfirmed) => {
              if (isConfirmed) {
                refreshWallets();
              }
              setShowPgwFooter(true);
            }}
          />
        ) : (
          <>
            <div
              className="text-center"
              style={isBrowser ? { marginTop: '50%' } : { marginTop: '50%', marginBottom: '50%' }}>
              <FocusableInputArea className="h1 amount-input">
                <input
                  required
                  min="0"
                  type="text"
                  className="form-control bg-transparent text-bold p-0 text-center border-0"
                  value={fiat.displayAmount || fiat.amount}
                  style={{
                    fontSize: 'inherit',
                    width: `${fiatInputWidth + 2}ch`,
                    color: 'inherit',
                    height: 'inherit',
                    textIndent: fiat.displayAmount ? 0 : '34%',
                  }}
                  ref={amountInputSpan}
                  onChange={(e) => {
                    setIsManualAmount(true);
                    handleAmountChangeInPGW(e);
                  }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="0  "
                />
                <b
                  style={{
                    fontSize: 'inherit',
                    pointerEvents: 'none',
                    color: 'inherit',
                  }}>
                  {wallet.coin.toUpperCase()}
                </b>
              </FocusableInputArea>
              <div className="d-flex flex-row-center mt-3">
                {PREDEFINED_AMOUNTS.map((amount) => (
                  <Badge
                    notifyRemoveActive={isManualAmount}
                    key={amount}
                    onClick={() => {
                      setIsManualAmount(false);
                      setFiat({ amount, displayAmount: amount });
                    }}
                    content={`${amount} ${wallet.coin.toUpperCase()}`}
                    className="py-1 px-3 text-white rounded-4 text-f6 cursor-pointer flex-row-center"
                    style={{
                      height: '1.75rem',
                      padding: `0.375rem ${wallet.coin.length <= 3 ? '1rem' : '0.75rem'}`,
                    }}
                  />
                ))}
              </div>
            </div>
            {showPgwWarning && (
              <div className="alert alert-info mb-4 mt-4">
                <i className="icon icon-info-icon" />
                <span className="content">{pgwWarningMessage}</span>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <ModalBodyLayout
      withBack={false}
      renderTitle={!amountChosen && renderTitle}
      renderFooter={(pgwUrl && showPgwFooter) || !pgwUrl || !amountChosen ? renderFooter : null}>
      <div>
        {amountChosen ? (
          <div className="d-flex flex-row">
            <i
              onClick={() => setAmountChosen(false)}
              onKeyDown={() => setAmountChosen(false)}
              className="icon icon-arrow-back-navigation text-f4 me-2"
              role="button"
              aria-label="Back"
              tabIndex={-1}
            />
            <div className="text-f5 fw-bold">
              {translate('card_order_proceed_with_payment_title')}
            </div>
          </div>
        ) : (
          <div className="flex-row-center bg-lighter border border-shadow rounded-4 my-4">
            <button
              className={`btn toggle-item toggle-item-modal ${
                isBank && 'active'
              } py-2 rounded-4 flex-fill m-0`}
              type="button"
              disabled={!canFundAccountsWithBankTransfer}
              onClick={() => {
                if (canFundAccountsWithBankTransfer) {
                  setIsBank(true);
                }
              }}>
              <b>{translate('tab1_fiat_deposit_label')}</b>
            </button>
            <button
              className={`btn toggle-item toggle-item-modal m-0 ${
                !isBank && 'active'
              } py-2 rounded-4 flex-fill`}
              type="button"
              disabled={!isAllowBuyPGW || !feauterPgwAllowed}
              onClick={() => setIsBank(false)}>
              <b>{translate('tab2_fiat_deposit_label')}</b>
            </button>
          </div>
        )}
        {isBank ? renderBank() : renderPgw()}
      </div>
    </ModalBodyLayout>
  );
};

export default FiatWalletDeposit;

FiatWalletDeposit.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  options: PropTypes.object,
  refreshWallets: PropTypes.func,
};
FiatWalletDeposit.defaultProps = {
  options: null,
  refreshWallets: () => {},
};
